import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/homeData';

export default function BasetaoSpreadsheetTutorial() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">Tutorial</span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">BaseTao Spreadsheet Tutorial: Step-by-Step for Beginners</h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">A complete beginner-friendly tutorial to set up and master your BaseTao spreadsheet from scratch.</p>
          </div>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Getting Started with BaseTao Spreadsheet</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">Welcome to this <strong className="text-foreground-900">BaseTao Spreadsheet tutorial</strong>. Whether you are a first-time BaseTao user or a seasoned shopper looking to get more organized, this guide will walk you through every step of setting up and using your spreadsheet effectively.</p>
              <p className="text-foreground-600 leading-relaxed">Before diving in, have the BaseTao Spreadsheet main website open in another tab. You will want to browse products and categories as you fill in your spreadsheet.</p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Step 1: Create Your Column Structure</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">Open your preferred spreadsheet software and create these essential columns:</p>
              <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
                <table className="w-full text-sm">
                  <thead><tr className="bg-background-100"><th className="text-left px-4 py-2.5 font-heading text-foreground-900 text-xs">Column</th><th className="text-left px-4 py-2.5 font-heading text-foreground-900 text-xs">Purpose</th><th className="text-left px-4 py-2.5 font-heading text-foreground-900 text-xs">Example</th></tr></thead>
                  <tbody className="divide-y divide-background-200/60">
                    {[{col:'Product Name',pur:'Identify each item',ex:'Nike Air Max 270'},{col:'Category',pur:'Group by type',ex:'Shoes'},{col:'Price (USD)',pur:'Track item cost',ex:'$42.00'},{col:'Shipping Est.',pur:'Estimate delivery cost',ex:'$18.50'},{col:'Warehouse Status',pur:'Track storage progress',ex:'Stored'},{col:'Final Cost',pur:'Calculate total spend',ex:'=$42+$18.50'}].map((r,i)=>(<tr key={i}><td className="px-4 py-2.5 text-foreground-700 font-medium text-xs">{r.col}</td><td className="px-4 py-2.5 text-foreground-500 text-xs">{r.pur}</td><td className="px-4 py-2.5 text-foreground-500 text-xs">{r.ex}</td></tr>))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Step 2: Add Your First Products</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">Browse the product categories on the main site and start adding items to your spreadsheet. For each product, fill in the name, category, and listed price. Leave the shipping estimate and warehouse status blank for now — you will update those as your order progresses.</p>
              <p className="text-foreground-600 leading-relaxed">Pro tip: Use a separate sheet tab for each shopping haul. Label them by date or theme so you can easily reference past orders.</p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Step 3: Set Up Formulas</h2>
              <p className="text-foreground-600 leading-relaxed">Create a SUM formula at the bottom of your price column to track total spending. Add a conditional formatting rule to highlight items with &quot;Ready&quot; warehouse status in green. These small automation touches make your BaseTao Spreadsheet far more powerful than a simple list. For advanced techniques, see our <Link to="/basetao-cost-tracker/" className="text-primary-600 hover:text-primary-700 font-medium">Cost Tracker guide</Link>.</p>
            </section>

            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 mt-10">
              <h3 className="font-heading text-lg text-foreground-900 mb-3">Ready to Practice?</h3>
              <p className="text-foreground-600 text-sm mb-4">Open your spreadsheet and start adding products from the main BaseTao site now.</p>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">Browse Products <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-sm"></i></div></a>
            </div>

            <div className="text-sm text-foreground-400 mt-8 pt-6 border-t border-background-200/60 flex flex-wrap gap-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">&larr; Back to Home</Link>
              <Link to="/basetao-spreadsheet/" className="hover:text-primary-600 transition-colors">Full Spreadsheet Guide</Link>
              <Link to="/basetao-spreadsheet-template/" className="hover:text-primary-600 transition-colors">Templates</Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'BaseTao Spreadsheet Tutorial: Step-by-Step for Beginners',
            description: 'A complete beginner-friendly tutorial to set up and master your BaseTao spreadsheet from scratch.',
            dateModified: '2026-08-10',
            datePublished: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://basetaosheet.team/basetao-spreadsheet-tutorial/',
            },
          }),
        }}
      />
    </div>
  );
}